import axios from "axios";
import { getCookie, removeCookie, setCookie } from "react-use-cookie";
import { getSsoReloginUrl } from "./auth.service";
import { logAuthDiag } from "./authClient";

/**
 * GEN-4150 — renovação automática da sessão de quem entrou pelo broker de SSO.
 *
 * Clientes federados (Bradesco, MRV, SG) entram por `sso.taxly.com.br`, que autentica no IdP do
 * cliente e volta para `/login?token=…`. Quando esse token expira, o produto manda a pessoa para
 * `/logout?expired=1` → `/login` — a tela de SENHA, inútil para quem só entra por SSO (o backend
 * responde `LOGIN_SSO_GATEWAY`). Ninguém guardava de onde a sessão veio.
 *
 * Agora o backend devolve `&sso=<key>` junto do token (GEN-4150, só para cliente habilitado), e este
 * módulo guarda a chave num cookie DURÁVEL para, na próxima expiração, mandar o navegador de volta
 * ao broker. Com a sessão do IdP viva, a volta é transparente.
 *
 * Três regras que não podem ser quebradas:
 *
 * 1. **Só `expired=1` dispara o redirect.** Logout deliberado NÃO: o Entra re-autentica em silêncio,
 *    então auto-redirecionar no `/logout` tornaria impossível sair. Conta bloqueada/desativada
 *    também não — vai falhar de novo no IdP e a pessoa precisa ver o motivo real.
 * 2. **O front nunca navega para URL vinda de cookie ou query.** O cookie guarda só a CHAVE; a URL
 *    vem de `GET /auth/sso-relogin/:key`, que só responde para chave da allowlist do backend. Sem
 *    isso, um cookie plantado viraria open redirect.
 * 3. **Anti-loop.** Se o token que voltar do broker morrer de novo em menos de `RELOGIN_COOLDOWN_MS`,
 *    não redireciona: cai na tela de login com o link de SSO. Senão um bug no caminho viraria
 *    ping-pong infinito entre plataforma e IdP. O marcador é POR ABA (`sessionStorage`): duas abas
 *    com o mesmo token expiram juntas e cada uma renova por conta própria — isso não é loop. Só
 *    quando não há `sessionStorage` (o Bradesco bloqueia storage na origem, GEN-3111) o marcador cai
 *    num cookie curto, compartilhado entre abas: sem ele não haveria defesa nenhuma.
 *
 * Por que COOKIE e não localStorage para a chave: `Logout` faz `localStorage.clear()`, e o Bradesco
 * bloqueia storage na origem (GEN-3111) — o cookie é o único lugar que sobrevive aos dois. O cookie
 * reflete SEMPRE a última entrada: login por senha, MSAL ou `?token=` sem `&sso=` apagam a chave,
 * senão outra pessoa no mesmo navegador seria mandada ao IdP de um cliente que não é o dela.
 *
 * ⚠️ Nada disto é testável de ponta a ponta pela Taxly: não temos conta nos IdPs dos clientes. A
 * prova real vem da telemetria (`logAuthDiag("sso-relogin:*")` ↔ `SSO_RELOGIN_RESOLVED` no backend)
 * depois do deploy, cliente a cliente. Por isso a decisão está isolada em funções puras testáveis.
 */

export const SSO_CLIENT_COOKIE = "sso_client";

/** Mesma régua do backend (`SSO_CLIENT_KEY`): minúsculas, dígitos e hífen, até 40 caracteres. */
const SSO_CLIENT_KEY = /^[a-z0-9][a-z0-9-]{0,38}$/;

/** Um ano: a chave do cliente não muda; o cookie só sai se a pessoa limpar o navegador ou entrar de outro jeito. */
const SSO_CLIENT_COOKIE_DAYS = 365;

/** Dois `expired=1` em menos disto = provável loop; o segundo não redireciona. */
export const RELOGIN_COOLDOWN_MS = 60_000;

/**
 * Janela em que um `?token=` novo ainda conta como "volta do relogin" para a telemetria. Acima disso
 * o marcador é de uma renovação antiga e o login é outro evento.
 */
export const RELOGIN_COMPLETION_WINDOW_MS = 10 * 60_000;

/** Quanto o `/logout` espera o backend responder a URL antes de desistir e cair na tela de login. */
export const RELOGIN_RESOLVE_TIMEOUT_MS = 5_000;

/** Instante do último redirect ao broker (cooldown anti-loop, por aba). Nunca é consumido — só expira. */
const RELOGIN_AT_KEY = "taxly:ssoReloginAt";
/** Cópia do cooldown em cookie curto, SÓ para quem não tem sessionStorage. */
const RELOGIN_AT_COOKIE = "sso_relogin_at";
const RELOGIN_AT_COOKIE_DAYS = 2 / (24 * 60); // 2 minutos
/** Redirect efetivamente disparado, pendente de confirmação: consumido UMA vez pelo `?token=` que volta. */
const RELOGIN_PENDING_KEY = "taxly:ssoReloginPending";

export type SsoReloginDecision = "renovar" | "suprimido" | "nao";

export const isValidSsoKey = (value: unknown): value is string =>
  typeof value === "string" && SSO_CLIENT_KEY.test(value);

const cookieOptions = (days: number) => ({
  days,
  path: "/",
  SameSite: "Lax" as const,
  Secure: typeof window !== "undefined" && window.location.protocol === "https:",
});

const readCookie = (name: string): string | null => {
  try {
    return getCookie(name) || null;
  } catch {
    return null;
  }
};

/** Lê a chave do cookie `sso_client`; `null` se ausente ou fora da régua (cookie adulterado). */
export const getSsoClient = (): string | null => {
  const value = readCookie(SSO_CLIENT_COOKIE);
  return isValidSsoKey(value) ? value : null;
};

/** Guarda a chave do cliente vinda do `&sso=` do backend. Valor fora da régua é ignorado. */
export const rememberSsoClient = (key: unknown): void => {
  if (!isValidSsoKey(key)) return;
  try {
    setCookie(SSO_CLIENT_COOKIE, key, cookieOptions(SSO_CLIENT_COOKIE_DAYS));
  } catch {
    /* cookie bloqueado — a pessoa cai no fluxo antigo quando expirar */
  }
};

/**
 * Apaga a chave. Chamado em todo login que NÃO veio do broker (senha, MSAL, `?token=` sem `&sso=`):
 * o cookie tem que dizer de onde veio a ÚLTIMA sessão, não a de alguém que usou o navegador antes.
 */
export const clearSsoClient = (): void => {
  try {
    removeCookie(SSO_CLIENT_COOKIE);
  } catch {
    /* noop */
  }
};

/**
 * Última entrada vinda do broker: grava a chave se veio `&sso=` válido, senão apaga a que havia.
 * É o que o consumo do `?token=` chama — um único ponto para a regra "o cookie reflete a última entrada".
 */
export const syncSsoClientFromLogin = (ssoParam: unknown): void => {
  if (isValidSsoKey(ssoParam)) rememberSsoClient(ssoParam);
  else clearSsoClient();
};

const parseTs = (raw: string | null): number | null => {
  const at = raw ? Number(raw) : NaN;
  return Number.isFinite(at) ? at : null;
};

const sessionGet = (key: string): string | null | undefined => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return undefined; // sem storage — diferente de "sem valor"
  }
};

/**
 * Instante do último redirect ao broker NESTA aba, ou `null`. O cookie (só gravado quando a ESCRITA
 * no storage falhou) entra sempre na conta: leitura e escrita podem falhar de forma independente
 * (storage legível mas sem quota), e um cookie gravado que nunca fosse lido reabriria o loop.
 * Num navegador normal o cookie não existe, então a aba vizinha não é afetada.
 */
export const peekSsoReloginAttempt = (): number | null => {
  const fromSession = parseTs(sessionGet(RELOGIN_AT_KEY) ?? null);
  const fromCookie = parseTs(readCookie(RELOGIN_AT_COOKIE));
  if (fromSession === null) return fromCookie;
  if (fromCookie === null) return fromSession;
  return Math.max(fromSession, fromCookie);
};

/**
 * Marca o cooldown anti-loop. Chamar ANTES de qualquer `await` cujo erro possa mandar de volta a
 * `/logout?expired=1` — e antes de navegar. Não é a pendência de telemetria (ver `markSsoReloginPending`).
 */
export const markSsoReloginAttempt = (now = Date.now()): void => {
  try {
    sessionStorage.setItem(RELOGIN_AT_KEY, String(now));
    return;
  } catch {
    /* sem sessionStorage — cai no cookie */
  }
  try {
    setCookie(RELOGIN_AT_COOKIE, String(now), cookieOptions(RELOGIN_AT_COOKIE_DAYS));
  } catch {
    /* sem storage e sem cookie: não há anti-loop; o backend ainda limita por IP */
  }
};

/**
 * Marca que o redirect ao broker FOI disparado (imediatamente antes do `assign`). É o que o
 * `?token=` que volta consome para emitir `sso-relogin:completed` — por isso só é gravado quando a
 * URL já está em mãos: uma resolução que falha não pode deixar pendência órfã, senão o próximo
 * `?token=` de qualquer origem viraria um `completed` falso na métrica que prova a feature.
 */
export const markSsoReloginPending = (now = Date.now()): void => {
  try {
    sessionStorage.setItem(RELOGIN_PENDING_KEY, String(now));
  } catch {
    /* sem storage não há `completed`; o `SSO_RELOGIN_RESOLVED` do backend segue contando */
  }
};

const clearSsoReloginPending = (): void => {
  try {
    sessionStorage.removeItem(RELOGIN_PENDING_KEY);
  } catch {
    /* noop */
  }
};

/**
 * Decisão pura de `/logout`:
 *  - `renovar`: sessão expirou, veio do broker e não houve tentativa recente → redirecionar;
 *  - `suprimido`: idem, mas houve tentativa há menos de `RELOGIN_COOLDOWN_MS` → provável loop,
 *    cair na tela de login (com o link de SSO) e registrar;
 *  - `nao`: não expirou (logout deliberado, bloqueio, desativação) ou não veio do broker.
 */
export const decidirRenovacaoPeloSso = (input: {
  expirou: boolean;
  ssoClient: string | null;
  lastAttemptAt: number | null;
  now?: number;
}): SsoReloginDecision => {
  if (!input.expirou || !input.ssoClient) return "nao";
  const now = input.now ?? Date.now();
  if (input.lastAttemptAt !== null && now - input.lastAttemptAt < RELOGIN_COOLDOWN_MS) return "suprimido";
  return "renovar";
};

/**
 * Só aceita o que o backend pode ter montado: URL absoluta `https:` — ou `http:` apenas quando a
 * própria página está em http (dev local). Defesa em profundidade: o endpoint já só devolve URL de
 * allowlist, mas uma resposta inesperada não pode virar navegação.
 */
export const isAcceptableReloginUrl = (value: unknown, pageProtocol?: string): value is string => {
  if (typeof value !== "string" || value.length === 0 || value.length > 2048) return false;
  try {
    const url = new URL(value);
    if (url.protocol === "https:") return true;
    const current = pageProtocol ?? (typeof window !== "undefined" ? window.location.protocol : "https:");
    return url.protocol === "http:" && current === "http:";
  } catch {
    return false;
  }
};

/**
 * URL de login do broker para a chave, ou `null` (404, rede, resposta inesperada, timeout). O
 * timeout é curto de propósito: enquanto isto roda a pessoa está numa tela em branco (`/logout`), e
 * o `ApiCall` padrão espera até 5 minutos. No timeout o GET é CANCELADO — uma resposta tardia 401
 * passaria pelo `handleErrorResponse` e arrancaria a pessoa do `/login` para `/logout?expired=1`.
 */
export const resolveSsoReloginUrl = async (
  key: string,
  timeoutMs = RELOGIN_RESOLVE_TIMEOUT_MS
): Promise<string | null> => {
  if (!isValidSsoKey(key)) return null;
  const source = axios.CancelToken.source();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => {
        source.cancel("sso-relogin timeout");
        resolve(null);
      }, timeoutMs);
    });
    const result = await Promise.race([getSsoReloginUrl(key, source.token), timeout]);
    if (!result || result.status !== 200) return null;
    const loginUrl = (result.response as { loginUrl?: unknown })?.loginUrl;
    return isAcceptableReloginUrl(loginUrl) ? loginUrl : null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * A sequência completa de ida ao broker, num lugar só (Logout e link "Entrar via SSO" usam a mesma):
 * cooldown ANTES do `await` → URL vinda do backend → pendência de telemetria → diag → `assign`.
 * Devolve `false` sem navegar quando não há URL (404 fora da allowlist, backend fora, timeout) — e
 * emite `sso-relogin:resolve-failed`; quem chama decide o que mostrar.
 */
export const irParaBroker = async (ssoClient: string, stage: "sso-relogin:redirect" | "sso-relogin:manual") => {
  markSsoReloginAttempt();
  const loginUrl = await resolveSsoReloginUrl(ssoClient);
  if (!loginUrl) {
    // `note` diz de onde veio (o `stage` do evento é o da falha); `reason` é o motivo.
    logAuthDiag("sso-relogin:resolve-failed", { ssoClient, reason: "no-url", note: stage });
    return false;
  }
  markSsoReloginPending();
  // Melhor esforço: o `assign` logo abaixo pode cancelar este POST. O contador confiável de
  // "redirect disparado" é o `SSO_RELOGIN_RESOLVED` que o backend loga ao responder a URL.
  logAuthDiag(stage, { ssoClient });
  try {
    window.location.assign(loginUrl);
    return true;
  } catch {
    // Navegação bloqueada (sandbox/iframe): sem redirect não pode sobrar pendência de `completed`,
    // e quem chamou precisa poder liberar a tela.
    clearSsoReloginPending();
    logAuthDiag("sso-relogin:resolve-failed", { ssoClient, reason: "assign-failed", note: stage });
    return false;
  }
};

/**
 * Quando um `?token=` novo chega: se há um redirect pendente e recente, devolve o tempo desde ele e
 * CONSOME a pendência (um redirect = no máximo um `completed`); senão `null`. O cooldown não é tocado.
 */
export const takeSsoReloginCompletion = (now = Date.now()): number | null => {
  try {
    const at = parseTs(sessionStorage.getItem(RELOGIN_PENDING_KEY));
    if (at === null) return null;
    sessionStorage.removeItem(RELOGIN_PENDING_KEY);
    const elapsed = now - at;
    return elapsed >= 0 && elapsed <= RELOGIN_COMPLETION_WINDOW_MS ? elapsed : null;
  } catch {
    return null;
  }
};
